import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { applyPage, pathFor, findEl, textNodeOf, setEditMode } from "@/editor/overrides";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const ORIGIN = window.location.origin;
export const EDIT_MODE = new URLSearchParams(window.location.search).get("edit") === "1" && window.parent !== window;

let pages = {};
let timer = null;
const applyNow = () => applyPage(pages[window.location.pathname] || {});
const schedule = () => { clearTimeout(timer); timer = setTimeout(applyNow, 80); };

const box = (color, dashed) => {
  const d = document.createElement("div");
  d.setAttribute("data-at-ui", "1");
  Object.assign(d.style, { position: "fixed", pointerEvents: "none", zIndex: 99999, border: `2px ${dashed ? "dashed" : "solid"} ${color}`, display: "none", transition: "none" });
  document.body.appendChild(d);
  return d;
};
const place = (d, el) => {
  if (!el || !el.isConnected) { d.style.display = "none"; return; }
  const r = el.getBoundingClientRect();
  Object.assign(d.style, { display: "block", left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
};

const TAGS = { h1: "Überschrift", h2: "Überschrift", h3: "Überschrift", h4: "Überschrift", p: "Text", span: "Text", img: "Bild",
  a: "Link / Knopf", button: "Knopf", section: "Bereich", nav: "Navigation", header: "Kopfzeile", footer: "Fußzeile", form: "Formular",
  input: "Eingabefeld", li: "Listeneintrag", ul: "Liste", svg: "Symbol" };
const labelFor = (el) => {
  const base = TAGS[el.tagName.toLowerCase()] || "Container";
  const t = (textNodeOf(el)?.nodeValue || el.getAttribute("data-testid") || "").trim().slice(0, 30);
  return t ? `${base}: ${t}` : base;
};

const infoFor = (el) => {
  const cs = getComputedStyle(el);
  const a = el.closest("a");
  return {
    path: pathFor(el), tag: el.tagName.toLowerCase(), label: labelFor(el),
    text: textNodeOf(el)?.nodeValue ?? null, isImg: el.tagName === "IMG", src: el.getAttribute("src") || "",
    href: el.getAttribute("data-at-href") || a?.getAttribute("data-at-href") || a?.getAttribute("href") || null,
    color: cs.color, bg: cs.backgroundColor, fontSize: parseFloat(cs.fontSize), align: cs.textAlign,
    added: el.hasAttribute("data-at-added"),
  };
};

const hex = (c) => {
  const m = (c || "").match(/\d+(\.\d+)?/g);
  if (!m || m.length < 3 || (m.length === 4 && Number(m[3]) === 0)) return undefined;
  return `#${m.slice(0, 3).map((x) => Number(x).toString(16).padStart(2, "0")).join("")}`;
};
const KIND = { img: "image", a: "button", button: "button", h1: "heading", h2: "heading", h3: "heading", h4: "heading", h5: "heading", h6: "heading" };
const describe = (el) => {
  const cs = getComputedStyle(el);
  const kind = KIND[el.tagName.toLowerCase()] || "text";
  const a = el.closest("a");
  return {
    path: pathFor(el), kind, text: kind === "image" ? "" : (el.innerText || "").trim().slice(0, 2000),
    src: el.getAttribute("src") || "", href: a?.getAttribute("data-at-href") || a?.getAttribute("href") || "",
    color: hex(cs.color), fontSize: Math.round(parseFloat(cs.fontSize)) || undefined, fontWeight: cs.fontWeight,
    width: kind === "image" ? Math.round(el.getBoundingClientRect().width) : undefined,
  };
};

const startEditAgent = () => {
  const hover = box("#EFDF00", true);
  const sel = box("#EFDF00", false);
  const line = box("#000", false);
  line.style.border = "0"; line.style.background = "#111";
  let selected = null;
  let mode = "snap";
  let drag = null;
  let multi = [];
  let extra = [];
  const boxes = [];
  const send = (msg) => window.parent.postMessage(msg, ORIGIN);
  const pick = (t) => (t && !t.closest("[data-at-ui]") && t !== document.body && t !== document.documentElement ? t.closest("[data-at-added]") || t : null);
  const els = () => pages[window.location.pathname]?.elements || {};
  const members = (el) => {
    const g = el && els()[pathFor(el)]?.g;
    return g ? Object.entries(els()).filter(([, o]) => o.g === g).map(([k]) => findEl(k)).filter(Boolean) : [];
  };
  const groupOf = (el) => {
    for (let cur = el; cur && cur !== document.body; cur = cur.parentElement) if (members(cur).length) return cur;
    return null;
  };
  const showExtra = (list) => {
    extra = list;
    while (boxes.length < list.length) { const b = box("#FF8A00", false); b.setAttribute("data-testid", "editor-group-box"); boxes.push(b); }
    boxes.forEach((b, i) => place(b, list[i] || null));
  };
  const refreshExtra = () => showExtra(multi.length ? multi : members(selected));
  const select = (el) => { selected = el; place(sel, el); refreshExtra(); send({ type: "at-ve-selected", info: el ? infoFor(el) : null }); };
  const setMulti = (list) => { multi = list; refreshExtra(); send({ type: "at-ve-multi", paths: list.map(pathFor) }); };

  const block = (e) => { if (!e.target.closest?.("[data-at-ui]")) { e.preventDefault(); e.stopPropagation(); } };
  ["click", "submit", "auxclick"].forEach((ev) => document.addEventListener(ev, block, true));
  document.addEventListener("dblclick", (e) => { block(e); const t = pick(e.target); const el = groupOf(t) || t; if (el) { select(el); send({ type: "at-ve-open", info: infoFor(el) }); } }, true);
  document.addEventListener("mousemove", (e) => {
    if (!drag) { place(hover, pick(e.target)); return; }
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    if (!drag.moving && Math.hypot(dx, dy) < 5) return;
    drag.moving = true;
    hover.style.display = "none";
    if (drag.group) {
      drag.group.forEach((m) => {
        m.el.style.translate = `${m.dx0 + dx}px ${m.dy0 + dy}px`;
        if (getComputedStyle(m.el).position === "static") m.el.style.position = "relative";
      });
      return;
    }
    if (mode === "free") {
      drag.el.style.translate = `${drag.dx0 + dx}px ${drag.dy0 + dy}px`;
      if (getComputedStyle(drag.el).position === "static") drag.el.style.position = "relative";
      place(sel, drag.el);
      return;
    }
    const under = document.elementFromPoint(e.clientX, e.clientY);
    let t = under;
    while (t && t.parentElement !== drag.el.parentElement) t = t.parentElement;
    if (!t || t === drag.el || t.hasAttribute?.("data-at-ui")) { line.style.display = "none"; drag.target = null; return; }
    const r = t.getBoundingClientRect();
    const pcs = getComputedStyle(drag.el.parentElement);
    const row = (pcs.display.includes("flex") && pcs.flexDirection.startsWith("row"))
      || (pcs.display.includes("grid") && pcs.gridTemplateColumns.split(" ").length > 1);
    const after = row ? e.clientX > r.left + r.width / 2 : e.clientY > r.top + r.height / 2;
    drag.target = { el: t, after };
    Object.assign(line.style, row
      ? { display: "block", left: `${after ? r.right : r.left - 2}px`, top: `${r.top}px`, width: "4px", height: `${r.height}px` }
      : { display: "block", left: `${r.left}px`, top: `${after ? r.bottom : r.top - 2}px`, width: `${r.width}px`, height: "4px" });
  }, true);
  document.addEventListener("mousedown", (e) => {
    let el = pick(e.target);
    if (!el || e.button !== 0) return;
    e.preventDefault();
    window.focus();
    const grp = groupOf(el);
    if (grp) el = grp;
    else if (selected && selected !== el && selected.contains(el)) el = selected;
    else if (mode === "snap") {
      while (el.parentElement && el.parentElement !== document.body
        && [...el.parentElement.children].filter((c) => !c.hasAttribute("data-at-ui")).length < 2) el = el.parentElement;
    }
    if (e.shiftKey) {
      const base = multi.length ? multi : selected ? [selected] : [];
      setMulti(base.includes(el) ? base.filter((m) => m !== el) : [...base, el]);
      return;
    }
    if (multi.length) setMulti([]);
    select(el);
    const off = (n) => { const [x, y] = (n.style.translate || "0px 0px").split(" ").map((v) => parseFloat(v) || 0); return [x, y || 0]; };
    const [dx0, dy0] = off(el);
    const group = grp ? members(el).map((m) => { const [a, b] = off(m); return { el: m, dx0: a, dy0: b }; }) : null;
    drag = { el, x: e.clientX, y: e.clientY, dx0, dy0, moving: false, group };
  }, true);
  document.addEventListener("mouseup", (e) => {
    if (!drag) return;
    const d = drag;
    drag = null;
    line.style.display = "none";
    if (!d.moving) return;
    if (d.group) {
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      send({ type: "at-ve-change", changes: Object.fromEntries(d.group.map((m) => [pathFor(m.el), { dx: Math.round(m.dx0 + dx), dy: Math.round(m.dy0 + dy) }])) });
    } else if (mode === "free") {
      send({ type: "at-ve-change", changes: { [pathFor(d.el)]: { dx: Math.round(d.dx0 + e.clientX - d.x), dy: Math.round(d.dy0 + e.clientY - d.y) } } });
    } else if (d.target) {
      const sibs = [...d.el.parentElement.children].filter((c) => !c.hasAttribute("data-at-ui"))
        .map((c, i) => ({ c, o: Number(c.style.order || 0) * 1000 + i })).sort((a, b) => a.o - b.o).map((x) => x.c)
        .filter((c) => c !== d.el);
      sibs.splice(sibs.indexOf(d.target.el) + (d.target.after ? 1 : 0), 0, d.el);
      send({ type: "at-ve-change", changes: Object.fromEntries(sibs.map((c, i) => [pathFor(c), { order: i * 10 }])) });
    }
  }, true);
  const group = () => (members(selected).length ? members(selected) : [selected]);
  const duplicate = () => { if (selected) send({ type: "at-ve-dup", items: group().map(describe) }); };
  const ARROWS = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
  document.addEventListener("keydown", (e) => {
    if (!selected || ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
    if (ARROWS[e.key]) {
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const [x, y] = ARROWS[e.key];
      send({ type: "at-ve-nudge", paths: group().map(pathFor), x: x * step, y: y * step });
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") { e.preventDefault(); duplicate(); return; }
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); send({ type: "at-ve-delete", path: pathFor(selected) }); }
    if (e.key === "Escape" && selected.parentElement !== document.body) select(selected.parentElement);
  }, true);
  window.addEventListener("message", (e) => {
    if (e.origin !== ORIGIN || !e.data) return;
    if (e.data.type === "at-ve-data") { pages = e.data.pages || {}; applyNow(); setTimeout(() => { if (selected) place(sel, selected); refreshExtra(); }, 50); }
    if (e.data.type === "at-ve-mode") mode = e.data.mode;
    if (e.data.type === "at-ve-parent" && selected?.parentElement && selected.parentElement !== document.body) { select(selected.parentElement); send({ type: "at-ve-open", info: infoFor(selected) }); }
    if (e.data.type === "at-ve-select") { multi = []; select(findEl(e.data.path)); }
    if (e.data.type === "at-ve-clear-multi") setMulti([]);
    if (e.data.type === "at-ve-dup-start") duplicate();
  });
  const follow = () => {
    if (selected) place(sel, selected.isConnected ? selected : null);
    boxes.forEach((b, i) => place(b, extra[i]?.isConnected ? extra[i] : null));
    requestAnimationFrame(follow);
  };
  follow();
  const style = document.createElement("style");
  style.setAttribute("data-at-ui", "1");
  style.textContent = "*{cursor:default!important;user-select:none} [data-at-added]{outline:1px dotted #bbb}";
  document.head.appendChild(style);
  send({ type: "at-ve-ready", path: window.location.pathname });
};

export const OverrideLayer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const obs = new MutationObserver((muts) => {
      if (muts.some((m) => [...m.addedNodes, ...m.removedNodes].some((n) => n.nodeType === 1 && !n.hasAttribute?.("data-at-ui")))) schedule();
    });
    obs.observe(document.body, { childList: true, subtree: true });
    if (EDIT_MODE) { setEditMode(true); startEditAgent(); }
    else {
      axios.get(`${API}/page-overrides`).then((r) => { pages = r.data || {}; schedule(); }).catch(() => {});
      document.addEventListener("click", (e) => {
        const a = e.target.closest?.("[data-at-href]");
        if (!a) return;
        e.preventDefault();
        e.stopPropagation();
        const href = a.getAttribute("data-at-href");
        if (href.startsWith("/")) navigate(href); else window.open(href, "_blank", "noopener");
      }, true);
    }
    return () => obs.disconnect();
  }, [navigate]);

  useEffect(() => { schedule(); setTimeout(applyNow, 600); }, [location.pathname]);
  return null;
};
